import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

import { loadTenantConfig } from "./tenant.local";
import { TenantConfig, defaultTenantConfig } from "./tenant.schema";
import { env } from "../../config/env";

interface TenantContextValue {
  tenant: TenantConfig;
  productId: string;
  clientId: string;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function applyThemeVariables(theme: TenantConfig["theme"]) {
  const root = document.documentElement;

  if (theme.primary) {
    root.style.setProperty("--tenant-primary", theme.primary);
  }
  if (theme.radius) {
    root.style.setProperty("--tenant-radius", theme.radius);
  }
}

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig>(
    defaultTenantConfig(env.PRODUCT_ID),
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function init() {
      const config = await loadTenantConfig(
        env.PRODUCT_ID,
        env.CLIENT_ID || undefined,
      );

      if (mounted) {
        setTenant(config);
        applyThemeVariables(config.theme);
        setIsLoading(false);
      }
    }

    init();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        productId: env.PRODUCT_ID,
        clientId: env.CLIENT_ID,
        isLoading,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }

  return context;
}
